import React from "react";
import { IoChevronForward } from "react-icons/io5";
import type { CtaEntry } from "../../../libs/cta";

export default function CtaCarousel({
  ctas,
  onCtaClick,
}: {
  ctas: CtaEntry[];
  onCtaClick: (cta: CtaEntry) => void;
}) {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const isDragging = React.useRef(false);
  const didDrag = React.useRef(false);
  const startX = React.useRef(0);
  const scrollLeft = React.useRef(0);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = trackRef.current;
    if (!el) return;
    isDragging.current = true;
    didDrag.current = false;
    startX.current = e.pageX - el.offsetLeft;
    scrollLeft.current = el.scrollLeft;
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    const el = trackRef.current;
    if (!el) return;
    const delta = e.pageX - el.offsetLeft - startX.current;
    if (Math.abs(delta) > 4) didDrag.current = true;
    el.scrollLeft = scrollLeft.current - delta;
  };

  const onMouseUp = () => {
    isDragging.current = false;
    const el = trackRef.current;
    if (!el) return;
    el.style.cursor = "grab";
    el.style.userSelect = "";
  };

  const handleItemClick = (e: React.MouseEvent, cta: CtaEntry) => {
    e.preventDefault();
    if (didDrag.current) return;
    onCtaClick(cta);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "2px", marginTop: "4px" }}>
      <div
        ref={trackRef}
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          overflowX: "auto",
          overflowY: "hidden",
          cursor: "grab",
          msOverflowStyle: "none",
          scrollbarWidth: "none",
          paddingBottom: "4px",
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        <ul style={{ display: "flex", gap: "12px", width: "max-content", listStyle: "none", margin: 0, padding: "4px 0" }}>
          {ctas.map((cta) => (
            <li key={cta.key} style={{ flexShrink: 0, display: "flex" }}>
              <a
                href={cta.link || "#"}
                {...(/^(tel:|mailto:|sms:)/i.test(cta.link || "") ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                draggable={false}
                data-otai-init="1"
                onClick={(e) => handleItemClick(e, cta)}
                onPointerDown={(e) => {
                  e.currentTarget.style.transform = "scale(0.95)";
                  e.currentTarget.style.boxShadow = "0 0 0 1.5px rgba(0, 0, 0, 0.08)";
                }}
                onMouseEnter={(e) => {
                  if (window.matchMedia?.("(hover: hover)").matches) e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
                onPointerUp={(e) => {
                  e.currentTarget.style.transform = e.pointerType === "mouse" ? "translateY(-2px)" : "scale(1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
                onPointerCancel={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
                aria-label={cta.title}
                style={{
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  width: "250px",
                  boxSizing: "border-box",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  lineHeight: 0,
                  userSelect: "none",
                  overflow: "hidden",
                  outline: "none",
                  WebkitTapHighlightColor: "transparent",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                }}
              >
                {cta.image ? (
                  <img
                    src={cta.image}
                    alt={cta.title}
                    draggable={false}
                    style={{ width: "100%", height: "auto", display: "block", pointerEvents: "none" }}
                  />
                ) : (
                  <span
                    style={{
                      width: "250px",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#fcfdfe",
                      padding: "16px",
                      textAlign: "center",
                      fontSize: "14px",
                      borderRadius: "8px",
                      boxSizing: "border-box",
                    }}
                  >
                    {cta.title}
                  </span>
                )}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <IoChevronForward style={{ width: "25px", height: "25px", color: "#333", flexShrink: 0, pointerEvents: "none" }} />
    </div>
  );
}
